/**
 * Styles compartilhados das telas de Orcamento (Fase 4 turno 4).
 *
 * Reusa tokens do design system Fluxo (cobre + grafite). `table-layout: fixed`
 * + `overflow-x: hidden` + ellipsis nas celulas longas — zero scroll lateral
 * constitucional.
 */
import styled from "styled-components";

import { mq } from "../../styles/canonical/breakpoints";
import { getToneTokens } from "../../styles/shared";

export const Card = styled.section`
  background: ${({ theme }) => theme.surface};
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 12px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const SectionTitle = styled.h3`
  font-size: 0.78rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: ${({ theme }) => theme.textSecondary};
`;

export const FilterBar = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
`;

export const SearchInput = styled.input`
  flex: 1;
  min-width: 220px;
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.surface};
  color: ${({ theme }) => theme.textPrimary};
  font-size: 0.88rem;

  &:focus {
    outline: 2px solid ${({ theme }) => theme.accent};
    outline-offset: -1px;
    border-color: transparent;
  }
`;

export const Select = styled.select`
  padding: 8px 10px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.surface};
  color: ${({ theme }) => theme.textPrimary};
  font-size: 0.85rem;
  cursor: pointer;
  /* Alvo de toque (auditoria mobile 2026-09-24, S5). */
  @media ${mq.mobile} {
    min-height: 40px;
  }
`;

export const PrimaryButton = styled.button`
  background: ${({ theme }) => theme.accent};
  color: var(--ink-on-accent);
  border: 0;
  border-radius: 8px;
  padding: 7px 14px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  @media ${mq.mobile} {
    font-size: 13px;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const SecondaryButton = styled.button`
  background: transparent;
  color: ${({ theme }) => theme.textPrimary};
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 8px;
  padding: 7px 14px;
  font-size: 0.85rem;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  @media ${mq.mobile} {
    font-size: 13px;
  }

  &:hover {
    background: ${({ theme }) => theme.surfaceAlt};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const DangerButton = styled(SecondaryButton)`
  color: ${({ theme }) => theme.dangerText};
  border-color: ${({ theme }) => theme.dangerBorder};

  &:hover {
    background: ${({ theme }) => theme.dangerSurface};
  }
`;

export const IconButton = styled.button`
  background: transparent;
  color: ${({ theme }) => theme.textSecondary};
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 6px;
  padding: 4px 8px;
  font-size: 0.75rem;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 4px;

  &:hover {
    color: ${({ theme }) => theme.textPrimary};
    background: ${({ theme }) => theme.surfaceAlt};
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

export const TableWrapper = styled.div`
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.surface};
  /* Bugfix 2026-05-29 (#13 fix v4 apos debug visual no navegador):
     ATENCAO ao CSS spec! Declarar APENAS overflow-x: hidden faz o
     browser auto-promover overflow-y para auto (regra do spec).
     Isso cria ancestral scrollavel fake, quebrando sticky do <th>
     interno (sem altura fixa, o wrapper nao rola, mas captura o
     sticky e o ancora a si mesmo, nao ao viewport).
     SOLUCAO: usar overflow-x: clip (que NAO auto-promove overflow-y).
     Suporte: Chrome 90+, Firefox 81+, Safari 16+, Edge 90+ — OK.
     Comportamento visual identico a hidden (impede scroll horizontal
     em colunas largas que excederiam o container). */
  overflow-x: clip;
`;

export const Table = styled.table`
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
  font-size: 0.88rem;

  th, td {
    text-align: left;
    padding: 8px 10px;
    border-bottom: 1px solid ${({ theme }) => theme.border};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  th {
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: ${({ theme }) => theme.textSecondary};
    font-weight: 600;
    /* Bugfix 2026-05-29 (#13 fix v2): sticky com background OPACO.
       TableWrapper tem overflow-y:auto local, mas o scroll efetivo
       na maioria das paginas que usam esse Table eh o do body —
       usar top:var(--app-header-height) padroniza com PainelTabela.
       Alpha removido (deixava linhas vazarem). */
    position: sticky;
    top: var(--app-header-height, 0px);
    z-index: 5;
    background: ${({ theme }) => theme.surfaceAlt};
  }

  tbody tr {
    cursor: ${({ $rowsClickable }) => ($rowsClickable ? "pointer" : "default")};
  }

  tbody tr:hover {
    background: ${({ theme }) => theme.surfaceAlt};
  }
`;

export const Pill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 600;
  background: ${({ theme, $tone = "neutral" }) =>
    getToneTokens(theme, $tone).background};
  color: ${({ theme, $tone = "neutral" }) => getToneTokens(theme, $tone).text};
  border: 1px solid
    ${({ theme, $tone = "neutral" }) => getToneTokens(theme, $tone).border};
  white-space: nowrap;
  /* Piso de leitura no celular (auditoria mobile 2026-09-24, S4). */
  @media ${mq.tablet} {
    font-size: var(--text-min);
  }
`;

export const EmptyMsg = styled.p`
  padding: 24px;
  text-align: center;
  color: ${({ theme }) => theme.textSecondary};
  font-size: 0.88rem;
`;

export const ErrorMsg = styled.div`
  padding: 12px 14px;
  border-radius: 8px;
  background: ${({ theme }) => theme.dangerSurface};
  color: ${({ theme }) => theme.dangerText};
  border: 1px solid ${({ theme }) => theme.dangerBorder};
  font-size: 0.85rem;
`;

export const InfoMsg = styled.div`
  padding: 12px 14px;
  border-radius: 8px;
  background: ${({ theme }) => theme.infoSurface};
  color: ${({ theme }) => theme.infoText};
  border: 1px solid ${({ theme }) => theme.infoBorder};
  font-size: 0.85rem;
  display: flex;
  align-items: center;
  gap: 6px;
`;

export const WarningMsg = styled(InfoMsg)`
  background: ${({ theme }) => theme.warningSurface};
  color: ${({ theme }) => theme.warningText};
  border-color: ${({ theme }) => theme.warningBorder};
`;

// ---------- Detalhe ----------

export const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;

  @media (min-width: 1100px) {
    grid-template-columns: 1.4fr 1fr;
  }
`;

/* Card de bloco — alinhado ao .block canônico (Bloco D): radius-xl + 18px. */
export const Block = styled.section`
  background: ${({ theme }) => theme.surface};
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: var(--radius-xl);
  padding: 18px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

export const FieldRow = styled.div`
  display: grid;
  grid-template-columns: 140px 1fr;
  gap: 8px;
  font-size: 0.88rem;
  align-items: baseline;

  @media (max-width: 540px) {
    grid-template-columns: 1fr;
    gap: 2px;
  }
`;

export const FieldLabel = styled.span`
  color: ${({ theme }) => theme.textSecondary};
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

export const FieldValue = styled.span`
  color: ${({ theme }) => theme.textPrimary};
  overflow-wrap: anywhere;
`;

// ---------- Editor de ambientes/itens ----------

export const AmbienteCard = styled.section`
  background: ${({ theme }) => theme.surface};
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 10px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const AmbienteHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

export const AmbienteNome = styled.h4`
  font-size: 1rem;
  font-weight: 600;
  color: ${({ theme }) => theme.textPrimary};
  flex: 1;
  margin: 0;
`;

export const ItemRow = styled.div`
  display: grid;
  /* minmax(0, …) e' obrigatorio: tracks fr tem min-width:auto por padrao,
     entao os inputs (number) e o total nao encolhem abaixo do conteudo e
     estouram a grid em containers estreitos (ex.: coluna esquerda do
     cockpit do pedido), invadindo o card vizinho. minmax(0,…) deixa
     encolher; min-width:0 nos filhos completa o ajuste. */
  grid-template-columns:
    minmax(0, 2.2fr) minmax(0, 0.7fr) minmax(0, 0.5fr)
    minmax(0, 0.8fr) minmax(0, 0.8fr) auto;
  gap: 6px;
  align-items: center;
  padding: 4px 0;
  border-top: 1px solid ${({ theme }) => theme.border};

  > input,
  > select {
    min-width: 0;
    width: 100%;
  }

  @media (max-width: 720px) {
    grid-template-columns: 1fr 1fr;
  }
`;

export const ItemTotal = styled.div`
  display: flex;
  justify-content: flex-end;
  font-size: 0.85rem;
  font-weight: 600;
  color: ${({ theme }) => theme.textPrimary};
  padding-top: 8px;
  border-top: 1px solid ${({ theme }) => theme.border};
`;

// ---------- Form ----------

export const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

export const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;

  > .full {
    grid-column: 1 / -1;
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

export const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 0.78rem;
  color: ${({ theme }) => theme.textSecondary};
`;

export const Input = styled.input`
  padding: 8px 10px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.surface};
  color: ${({ theme }) => theme.textPrimary};
  font-size: 0.88rem;

  &:focus {
    outline: 2px solid ${({ theme }) => theme.accent};
    outline-offset: -1px;
    border-color: transparent;
  }
`;

export const Textarea = styled.textarea`
  padding: 8px 10px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.surface};
  color: ${({ theme }) => theme.textPrimary};
  font-size: 0.88rem;
  min-height: 80px;
  resize: vertical;
  font-family: inherit;
`;

export const FormFooter = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  padding-top: 8px;
  border-top: 1px solid ${({ theme }) => theme.border};
`;

export const InlineHint = styled.span`
  font-size: 0.72rem;
  color: ${({ theme }) => theme.textTertiary};
`;

// ---------- ComposicaoPreco (Bloco E.2 v2 + Bloco F.1.1/F.2) ----------
// Substituem inline styles `var(--surface, white)` (que ficavam brancos no
// dark mode porque o app usa styled-components, nao CSS vars).

export const CascataCard = styled.section`
  background: ${({ theme }) => theme.surface};
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 12px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const CascataHeader = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  flex-wrap: wrap;
`;

export const CascataTitle = styled.h3`
  font-size: 0.78rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: ${({ theme }) => theme.textSecondary};
  margin: 0;
`;

export const CascataGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 4px 12px;
  font-size: 0.88rem;
  color: ${({ theme }) => theme.textPrimary};
`;

export const CascataLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 6px;
  color: ${({ theme }) => theme.textPrimary};
`;

export const CascataLabelMuted = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  color: ${({ theme }) => theme.textPrimary};
  opacity: 0.92;
`;

export const CascataValue = styled.div`
  text-align: right;
  font-weight: 500;
  color: ${({ theme }) => theme.textPrimary};
`;

export const CascataDelta = styled.div`
  text-align: right;
  color: ${({ theme }) => theme.infoText};
  font-weight: 500;
`;

export const CascataDeltaDesconto = styled.div`
  text-align: right;
  color: ${({ theme }) => theme.dangerText};
  font-weight: 500;
`;

export const CascataDivider = styled.div`
  grid-column: 1 / -1;
  border-top: 1px solid ${({ theme }) => theme.border};
  margin-top: 4px;
  padding-top: 2px;
`;

export const CascataDividerStrong = styled.div`
  grid-column: 1 / -1;
  border-top: 2px solid ${({ theme }) => theme.textPrimary};
  margin-top: 6px;
  padding-top: 2px;
`;

export const CascataSubtotalLabel = styled.div`
  font-weight: 600;
  color: ${({ theme }) => theme.textPrimary};
`;

export const CascataSubtotalValue = styled.div`
  text-align: right;
  font-weight: 600;
  color: ${({ theme }) => theme.textPrimary};
`;

export const CascataTotalLabel = styled.div`
  font-size: 1rem;
  font-weight: 700;
  color: ${({ theme }) => theme.textPrimary};
`;

export const CascataTotalValue = styled.div`
  font-size: 1rem;
  font-weight: 700;
  text-align: right;
  color: ${({ theme }) => theme.textPrimary};
`;

export const CascataFooter = styled.div`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.textSecondary};
  display: flex;
  align-items: center;
  gap: 4px;
`;

export const CascataInputNum = styled.input`
  width: 70px;
  padding: 4px 8px;
  border-radius: 6px;
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme, $invalido }) =>
    $invalido ? theme.dangerSurface : theme.surface};
  color: ${({ theme, $invalido }) =>
    $invalido ? theme.dangerText : theme.textPrimary};
  font-size: 0.85rem;

  &:focus {
    outline: 2px solid ${({ theme }) => theme.accent};
    outline-offset: -1px;
    border-color: transparent;
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

export const CascataReadOnlyValue = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 60px;
  padding: 4px 8px;
  border-radius: 6px;
  background: ${({ theme }) => theme.surfaceAlt};
  color: ${({ theme }) => theme.textPrimary};
  font-size: 0.85rem;
  font-weight: 500;
`;

export const CascataLinkButton = styled.button`
  background: none;
  border: 0;
  padding: 0;
  color: ${({ theme }) => theme.textPrimary};
  cursor: pointer;
  text-decoration: underline;
  font-size: inherit;
  text-align: left;

  &:disabled {
    cursor: default;
    text-decoration: none;
    opacity: 0.85;
  }
`;

export const CascataInlineNote = styled.span`
  font-size: 0.7rem;
  opacity: 0.7;
  color: ${({ theme }) => theme.textTertiary};
`;

export const CascataReadOnlyBanner = styled.div`
  padding: 10px 12px;
  border-radius: 8px;
  background: ${({ theme }) => theme.infoSurface};
  color: ${({ theme }) => theme.infoText};
  border: 1px solid ${({ theme }) => theme.infoBorder};
  font-size: 0.78rem;
  display: flex;
  align-items: flex-start;
  gap: 8px;
`;

export const CascataDescontoExcessivo = styled.div`
  padding: 10px 12px;
  border-radius: 8px;
  background: ${({ theme }) => theme.warningSurface};
  color: ${({ theme }) => theme.warningText};
  border: 1px solid ${({ theme }) => theme.warningBorder};
  font-size: 0.82rem;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

// ---------- Drill-down de composicao ----------

export const ComposicaoSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const ComposicaoSubtitle = styled.h4`
  font-size: 0.85rem;
  margin: 0;
  color: ${({ theme }) => theme.textPrimary};
  display: flex;
  align-items: center;
  gap: 4px;
`;

export const ComposicaoEmpty = styled.p`
  font-size: 0.85rem;
  color: ${({ theme }) => theme.textSecondary};
  margin: 0;
`;

export const ComposicaoTable = styled.table`
  width: 100%;
  font-size: 0.85rem;
  border-collapse: collapse;

  thead {
    background: ${({ theme }) => theme.surfaceAlt};
  }

  th,
  td {
    padding: 6px 8px;
    color: ${({ theme }) => theme.textPrimary};
  }

  th {
    text-align: left;
    font-weight: 600;
    color: ${({ theme }) => theme.textSecondary};
    text-transform: uppercase;
    letter-spacing: 0.04em;
    font-size: 0.72rem;
  }

  th.num,
  td.num {
    text-align: right;
  }

  tbody tr {
    border-bottom: 1px solid ${({ theme }) => theme.border};
  }

  tbody tr:last-child {
    border-bottom: 0;
  }
`;

export const ComposicaoPendenteTag = styled.span`
  margin-left: 6px;
  font-size: 0.7rem;
  color: ${({ theme }) => theme.warningText};
`;

export const ComposicaoTotalRow = styled.div`
  border-top: 2px solid ${({ theme }) => theme.textPrimary};
  padding-top: 8px;
  margin-top: 8px;
  display: flex;
  justify-content: space-between;
  font-size: 0.95rem;
  font-weight: 700;
  color: ${({ theme }) => theme.textPrimary};
`;

export const ComposicaoAvisosBox = styled.div`
  padding: 8px 12px;
  background: ${({ theme }) => theme.warningSurface};
  border: 1px solid ${({ theme }) => theme.warningBorder};
  border-radius: 6px;
  font-size: 0.78rem;
  color: ${({ theme }) => theme.warningText};

  ul {
    margin: 4px 0 0 16px;
    padding: 0;
  }
`;

/**
 * Banner informativo (azul) — usado no drill-down de composicao
 * para avisar que o calculo de chapas eh aproximado por area.
 *
 * Contexto: POC DinaBox (2026-05-23) confirmou que a API v1 nao
 * expoe plano de corte real. Ver docs/integracoes/dinabox-cutplan.md.
 * Estoque/PCP precisa rodar otimizacao no DinaBox antes de comprar.
 */
export const ComposicaoInfoBanner = styled.div`
  padding: 8px 12px;
  background: ${({ theme }) => theme.infoSurface};
  border: 1px solid ${({ theme }) => theme.infoBorder};
  border-radius: 6px;
  font-size: 0.78rem;
  color: ${({ theme }) => theme.infoText};
  line-height: 1.4;
`;

/**
 * Linha de ações da tabela de cotações.
 *
 * Reteste 2026-08-18: os botões vinham separados por `{" "}` — e o
 * separador existia entre o primeiro e o segundo, mas não entre o segundo
 * e o terceiro, produzindo "Tornar firmeExcluir" colado na tela. Espaço
 * entre controles é trabalho do layout, não de um espaço em branco no
 * meio do JSX.
 */
export const AcoesCotacao = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  justify-content: flex-start;
`;
